import React from 'react';
import { useState } from 'react';
import FirebaseService from '../services/FirebaseService';
import OrganizationDetails from '../components/organization/OrganizationDetails';

const VerifyOrganization = () => {
  const [tabValue, setTabValue] = useState(0); // 0 for certificate number, 1 for organization name
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [error, setError] = useState('');

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setOrganization(null);
    setOrganizations([]);
    setError('');
    setSearchTerm('');
  };

  const handleSearch = async () => {
    const trimmedSearchTerm = searchTerm.trim();
    
    if (!trimmedSearchTerm) {
      setError('Please enter a search term');
      return;
    }
    
    // Require at least 3 characters for search to prevent data exposure
    if (trimmedSearchTerm.length < 3) {
      setError('Please enter at least 3 characters for search');
      return;
    }

    setLoading(true);
    setError('');
    setOrganization(null);
    setOrganizations([]);

    try {
      if (tabValue === 0) { // Certificate Number search
        const result = await FirebaseService.getOrganizationByCertificateNumber(searchTerm.trim());
        if (result) {
          setOrganization(result);
        } else {
          setError('No accredited organization found with the provided certificate number');
        }
      } else { // Organization Name search
        const results = await FirebaseService.searchOrganizationsByName(searchTerm.trim());
        if (results.length > 0) {
          setOrganizations(results);
        } else {
          setError('No accredited organizations found with the provided name');
        }
      }
    } catch (err) {
      // Error searching organizations
      setError('An error occurred while searching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 16px 40px',
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    }}>
      {/* Main Card */}
      <div style={{
        width: '100%', maxWidth: '820px', backgroundColor: '#ffffff',
      }}>
        <div style={{ padding: '32px 0 0' }}>
          {/* Segmented toggle */}
          <div style={{
            display: 'inline-flex',
            backgroundColor: '#f0f2f5',
            borderRadius: '10px',
            padding: '4px',
            marginBottom: '28px',
            gap: '2px',
          }}>
            {[
              { label: 'Certificate No.', icon: '🔎' },
              { label: 'Organization Name', icon: '🏢' },
            ].map(({ label, icon }, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(index)}
                style={{
                  background: tabValue === index ? '#ffffff' : 'transparent',
                  border: 'none',
                  borderRadius: '7px',
                  color: tabValue === index ? '#0d2347' : '#8a95a3',
                  fontSize: '13.5px',
                  fontWeight: tabValue === index ? '600' : '400',
                  padding: '9px 20px',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.18s',
                  boxShadow: tabValue === index ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: '13px' }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>

          {/* Search Row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
            <input
              type="text"
              placeholder={tabValue === 0 ? 'Enter Certificate Number' : 'Enter Organization Name'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              style={{
                flex: 1, height: '52px', border: '1.5px solid #dde2ea', borderRadius: '8px',
                padding: '0 18px', fontSize: '15px', outline: 'none', color: '#0a1628',
                backgroundColor: '#f7f9fc', boxSizing: 'border-box', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#0d2347'}
              onBlur={e => e.target.style.borderColor = '#dde2ea'}
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              style={{
                height: '52px', minWidth: '130px',
                background: loading ? '#8a95a3' : 'linear-gradient(135deg, #0d2347 0%, #1a3a6e 100%)',
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px', padding: '0 24px',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(13,35,71,0.35)', transition: 'all 0.2s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search
            </button>
          </div>

          {error && (
            <p style={{ color: '#c0392b', fontSize: '13.5px', margin: '12px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </p>
          )}

          {organization && <OrganizationDetails organization={organization} />}

          {organizations.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '13px', color: '#8a95a3', marginBottom: '8px', fontWeight: '500' }}>
                Found {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
              </p>
              {organizations.map((org, index) => (
                <OrganizationDetails key={org.id || index} organization={org} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyOrganization;
